import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { FieldValues } from "react-hook-form";
import type { BillingSchedule, BreakdownMode } from "../api";
import {
  type PaymentPlanBreakdownInputSnapshot,
  pickPaymentPlanBreakdownInputSnapshot,
} from "../breakdownInputSnapshots";

type UsePaymentPlanBreakdownStalenessOptions = {
  plan: FieldValues | undefined;
  yearNumber: number;
  hasValidTorType: boolean;
  isTurnoverRentOnly: boolean;
  planStableId: string;
  breakdownRows: BillingSchedule[] | null;
  breakdownMode: BreakdownMode;
  onStaleChange?: (planId: string, stale: boolean) => void;
};

type UsePaymentPlanBreakdownStalenessReturn = {
  /**
   * Call immediately after a successful "Generate Breakdown" response.
   * Seeds the snapshot from the current form values so the breakdown is
   * not immediately considered stale.
   */
  onBreakdownSuccess: (currentPlan: FieldValues) => void;
  /** Call when breakdown rows are cleared (e.g. copy-from-previous-year). */
  onBreakdownCleared: () => void;
};

export function usePaymentPlanBreakdownStaleness(
  options: UsePaymentPlanBreakdownStalenessOptions,
): UsePaymentPlanBreakdownStalenessReturn {
  const {
    plan,
    yearNumber,
    hasValidTorType,
    isTurnoverRentOnly,
    planStableId,
    breakdownRows,
    breakdownMode,
    onStaleChange,
  } = options;

  const snapshotRef = useRef<PaymentPlanBreakdownInputSnapshot | null>(null);

  const currentPick = useMemo(
    () =>
      pickPaymentPlanBreakdownInputSnapshot({
        plan,
        yearNumber,
        hasValidTorType,
        isTurnoverRentOnly,
        breakdownMode,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plan, yearNumber, hasValidTorType, isTurnoverRentOnly, breakdownMode],
  );

  useEffect(() => {
    if (!breakdownRows?.length) {
      snapshotRef.current = null;
      onStaleChange?.(planStableId, false);
      return;
    }
    // First render after server-loaded breakdown: seed snapshot so it is not immediately stale.
    if (snapshotRef.current === null) {
      snapshotRef.current = currentPick;
      onStaleChange?.(planStableId, false);
      return;
    }
    onStaleChange?.(planStableId, !isEqual(currentPick, snapshotRef.current));
  }, [currentPick, breakdownRows?.length, planStableId, onStaleChange]);

  const onBreakdownSuccess = useCallback(
    (currentPlan: FieldValues) => {
      snapshotRef.current = pickPaymentPlanBreakdownInputSnapshot({
        plan: currentPlan,
        yearNumber,
        hasValidTorType,
        isTurnoverRentOnly,
        breakdownMode,
      });
      onStaleChange?.(planStableId, false);
    },
    [breakdownMode, hasValidTorType, isTurnoverRentOnly, onStaleChange, planStableId, yearNumber],
  );

  const onBreakdownCleared = useCallback(() => {
    snapshotRef.current = null;
    onStaleChange?.(planStableId, false);
  }, [onStaleChange, planStableId]);

  return { onBreakdownSuccess, onBreakdownCleared };
}
